import React, { useEffect, useRef } from 'react';
import { ToastContainer } from 'react-toastify';

import { useAppDispatch, useAppSelector } from './store/hooks';

import { Loader } from './components/loader/loader.component';
import { ImpersonationBanner } from './components/impersonation-banner/impersonation-banner.component';
import { BillingAddressPrompt } from './components/billing-address-prompt/billing-address-prompt.component';

import AppRoutes from './infra/routes/index';

import { selectIsLoading } from './store/loading/loading.slice';
import * as selectUser from './store/user/user.selector';
import { selectRefreshConsumerUnit } from './store/consumer_unit/consumer_unit.selector';
import { selectRefreshInvoice } from './store/invoice/invoice.selector';
import { setConsumerUnitRefresh } from './store/consumer_unit/consumer_unit.slice';

import { getAlertSettingsUserList } from './store/alert_settings_user/alert_settings_user.action';
import { getAlertList } from './store/alert/alert.action';
import { getClassificationClassList } from './store/classification_class/classification_class.action';
import { getClassificationSubgroupList } from './store/classification_subgroup/classification_subgroup.action';
import { getConnectionList } from './store/connection/connection.action';
import { getCurrentFlagList } from './store/current_flag/current_flag.action';
import { getEstablishmentList } from './store/establishment/establishment.action';
import { getLoadProfileList } from './store/load_profile/load_profile.action';
import { getMonitoringSystemList } from './store/monitoring_system/monitoring_system.action';
import { getStateList } from './store/state/state.action';
import { getUtilityCompanyList } from './store/utility_company/utility_company.action';
import { getVoltageList } from './store/voltage/voltage.action';
import { getConsumerUnitList } from './store/consumer_unit/consumer_unit.action';
import { getInvoiceList } from './store/invoice/invoice.action';
import { getMonitoringSystemUserList } from './store/monitoring_system_user/monitoring_system_user.action';
import { fetchCurrentUser } from './store/user/user.action';

/**
 * Raiz da aplicação: monta as rotas, o loader global e o ToastContainer.
 * Ao logar, hidrata o Redux com ~14 thunks (tabelas de referência + UCs +
 * faturas + monitoramento) — as páginas assumem esse estado já carregado.
 * As flags refreshConsumerUnit/refreshInvoice (setadas pelos slices após
 * mutações) disparam o recarregamento de UCs e faturas sem F5.
 */
// Refresh pedido logo após a hidratação do login reaproveita o que acabou de
// ser buscado.
const HYDRATION_GRACE_MS = 5000;

function App() {
  const dispatch = useAppDispatch();
  const isLoading = useAppSelector(selectIsLoading);
  const isLoggedIn = useAppSelector(selectUser.isLoggedIn);
  const refreshConsumerUnit = useAppSelector(selectRefreshConsumerUnit);
  const refreshInvoice = useAppSelector(selectRefreshInvoice);

  // Momento da hidratação do login: o Dashboard (destino pós-login) pede refresh
  // ao montar, e sem esta janela as UCs e as faturas eram buscadas DUAS vezes
  // em todo login.
  const hydratedAtRef = useRef(0);

  useEffect(() => {
    if (isLoggedIn) {
      hydratedAtRef.current = Date.now();
      // Re-hidrata o usuário logado (pega troca de plano feita pelo admin)
      dispatch(fetchCurrentUser());
      dispatch(getAlertSettingsUserList());
      dispatch(getAlertList());
      dispatch(getClassificationClassList());
      dispatch(getClassificationSubgroupList());
      dispatch(getConnectionList());
      dispatch(getEstablishmentList());
      dispatch(getLoadProfileList());
      dispatch(getMonitoringSystemList());
      dispatch(getStateList());
      dispatch(getUtilityCompanyList());
      dispatch(getVoltageList());
      dispatch(getInvoiceList());
      dispatch(getMonitoringSystemUserList());
      dispatch(getCurrentFlagList());
      dispatch(getConsumerUnitList());
    }
  }, [isLoggedIn, dispatch]);

  // Refresh após mutação (fatura salva/excluída, UC criada/editada/excluída) ou
  // ao voltar ao Dashboard: recarrega UCs E faturas. Até 30/set/2026 só as UCs
  // eram recarregadas — a lista de faturas do dashboard (GET /invoices) ficava
  // a do login, e depois de recriar UCs os cards mostravam "0/9 fatura(s)"
  // enquanto o Resumo somava as faturas antigas. (A flag de refresh de faturas
  // se auto-reseta no reducer; quem sinaliza é a de UCs.)
  useEffect(() => {
    if (isLoggedIn && (refreshConsumerUnit || refreshInvoice)) {
      if (Date.now() - hydratedAtRef.current > HYDRATION_GRACE_MS) {
        dispatch(getConsumerUnitList());
        dispatch(getInvoiceList());
      }
      dispatch(setConsumerUnitRefresh({ refresh: false }));
    }
  }, [isLoggedIn, refreshConsumerUnit, refreshInvoice, dispatch]);

  return (
    <>
      <ToastContainer />
      {isLoading && <Loader />}
      <AppRoutes />
      <ImpersonationBanner />
      {isLoggedIn && <BillingAddressPrompt />}
    </>
  );
}

export default App;
